export interface TsNode {
  id: string;
  name: string;
  category: string;
  dependents: number;
  exports?: string[];
  file?: string;
  /** Which side of the stack this node belongs to (set during merge). */
  stack?: 'frontend' | 'backend';
}

/**
 * Shared category → color map for both stacks. Frontend categories come from
 * src/ first-level dirs; backend categories from rust-backend/src/ dirs.
 * 'auth' and 'export' intentionally share one color across stacks.
 */
export const CATEGORY_COLORS: Record<string, string> = {
  // frontend
  component: '#6366f1',
  service: '#10b981',
  store: '#f59e0b',
  hook: '#8b5cf6',
  type: '#06b6d4',
  utility: '#f97316',
  constant: '#84cc16',
  page: '#ec4899',
  // backend (rust-backend/src/*)
  api: '#4f46e5',
  rlm: '#7c3aed',
  knowledge_graph: '#0891b2',
  issue_analysis: '#d97706',
  ingestion: '#059669',
  db: '#ea580c',
  models: '#65a30d',
  reasoning: '#db2777',
  retrieval: '#14b8a6',
  query: '#a855f7',
  utils: '#64748b',
  response: '#0ea5e9',
  evaluation: '#d946ef',
  pipeline: '#22c55e',
  cache: '#eab308',
  streaming: '#3b82f6',
  migrations: '#78716c',
  middleware: '#f43f5e',
  domain: '#a3e635',
  core: '#475569',
  // shared
  auth: '#ef4444',
  export: '#94a3b8',
  other: '#64748b',
};

export function getCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] || '#94a3b8';
}

export interface TsEdge {
  source: string;
  target: string;
  /** `calls` = cross-stack HTTP edge (frontend caller → backend handler). */
  type: 'imports' | 'uses' | 'circular' | 'calls';
  /** For `calls` edges: the matched endpoint path, e.g. "/schema/table/*". */
  endpoint?: string;
}

export interface TsGraphData {
  nodes: TsNode[];
  edges: TsEdge[];
  metadata: {
    filesScanned: number;
    nodesCount: number;
    edgesCount: number;
    unusedExports: Array<{ file: string; export: string }>;
    unusedExportsCount: number;
    circularDependencies: Array<{ nodes: string[] }>;
    circularDependenciesCount: number;
    /** Frontend only: HTTP endpoints each file calls (normalized paths). */
    apiCalls?: Array<{ file: string; path: string }>;
    /** Backend only: registered routes → handler file (normalized paths). */
    apiRoutes?: Array<{ path: string; file: string }>;
  };
}
